/* Reference points and exclusion zones.
 *
 * Reference points are named, movable geographic anchors (FR-SCEN-05). Missions consume
 * them to define areas, so a patrol area is "RP-ALPHA/BRAVO/CHARLIE/DELTA" rather than
 * four loose coordinates — move the reference point and the mission area moves with it.
 * That indirection is the whole reason they exist.
 *
 * Exclusion zones constrain missions: keep-out (do not enter) or weapons-free/tight
 * overrides scoped to an area.
 */

export interface RefPoint {
  id: string
  name: string
  lon: number
  lat: number
  side: 'blue' | 'red' | 'shared'
  /** Reference points bound into a named area render as a closed polygon. */
  area?: string
}

export type ZoneKind = 'keepout' | 'weapons_free' | 'weapons_tight' | 'no_fly'

export interface ExclusionZone {
  id: string
  name: string
  kind: ZoneKind
  side: 'blue' | 'red' | 'shared'
  /** Closed ring, [lon, lat]. */
  ring: [number, number][]
}

export const ZONE_LABEL: Record<ZoneKind, string> = {
  keepout: 'Keep-out',
  weapons_free: 'Weapons free',
  weapons_tight: 'Weapons tight',
  no_fly: 'No-fly',
}

/** Zone colour carries meaning, so it is defined once here and not per component. */
export const ZONE_COLOR: Record<ZoneKind, { line: string; fill: string }> = {
  keepout: { line: 'rgba(232,81,90,0.7)', fill: 'rgba(232,81,90,0.07)' },
  no_fly: { line: 'rgba(229,169,75,0.7)', fill: 'rgba(229,169,75,0.06)' },
  weapons_free: { line: 'rgba(79,196,138,0.65)', fill: 'rgba(79,196,138,0.06)' },
  weapons_tight: { line: 'rgba(177,138,224,0.65)', fill: 'rgba(177,138,224,0.06)' },
}

/* No seeded instances live here any more.
 *
 * Reference points and zones belong to the EXERCISE, so they arrive in the scenario document via
 * `GET /v1/scenario` and are side-scoped by the Gateway. What remains in this file is the schema
 * and the two lookup tables that are presentation decisions — the zone label and the zone colour,
 * which carry meaning and are therefore defined once rather than per component.
 */
